import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getPlayerAvatar } from '../../utils/player';
import serveBadge from '../../assets/serve_badge.svg';

// Helper for formatting player names to Title Case (e.g., "Caroline & David")
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => {
      if (word.toUpperCase() === '&' || word.toLowerCase() === '&') return '&';
      if (word.toLowerCase() === 'and') return '&';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const CONFETTI_COLOURS = ['#f5de7d', '#f4ca53', '#eaa938', '#b0c6ff', '#ffffff', '#4793cf'];

// Deterministic confetti pieces (stable across renders) spread across the screen width
const makeConfetti = (count) =>
  Array.from({ length: count }, (_, i) => {
    const seed = (i * 9301 + 49297) % 233280;
    const rand = (n) => ((seed * (n + 1)) % 1000) / 1000;
    return {
      left: `${(i / count) * 100 + rand(1) * 4 - 2}%`,
      delay: `${Math.round(rand(2) * 700)}ms`,
      duration: `${2200 + Math.round(rand(3) * 1600)}ms`,
      drift: `${Math.round(rand(4) * 120 - 60)}px`,
      spin: `${Math.round(rand(5) * 720 - 360)}deg`,
      size: `${6 + Math.round(rand(6) * 6)}px`,
      colour: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
    };
  });

const setScorePair = (s) => (Array.isArray(s) ? [s[0], s[1]] : [s?.team1 ?? 0, s?.team2 ?? 0]);

export default function CelebrationOverlay({ eventData, onDismiss, allPlayers = [] }) {
  const [visible, setVisible] = useState(true);
  const onDismissRef = useRef(onDismiss);
  const confetti = useMemo(() => makeConfetti(42), []);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const duration = !eventData ? 0 : eventData.mode === 'mvp' ? 6500 : eventData.type === 'MATCH' ? 6000 : eventData.type === 'SET' ? 5000 : 4500;

  useEffect(() => {
    if (!eventData) return;
    setVisible(true);

    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        if (onDismissRef.current) {
          onDismissRef.current();
        }
      }, 400);
    }, duration);

    return () => clearTimeout(timer);
  }, [eventData, duration]);

  if (!eventData) return null;

  const {
    team,
    teamAName,
    teamBName,
    teamAAvatars,
    teamBAvatars,
    teamAPlayers,
    teamBPlayers,
    server: currentServer,
    type = 'GAME',
    mode,
    nextGame,
    games,
    setScores,
  } = eventData;
  const isTeamA = team === 'A' || team === 1;

  const winnerName = isTeamA ? teamAName : teamBName;
  const rawAvatars = isTeamA ? teamAAvatars : teamBAvatars;
  const avatarsList = Array.isArray(rawAvatars) ? rawAvatars.filter(Boolean) : [];

  const p1Avatar = avatarsList[0] || getPlayerAvatar(allPlayers, winnerName);
  const p2Avatar = avatarsList.length > 1 ? avatarsList[1] : null;

  // Identify which winning player was serving in Classic mode (if any)
  const winningPlayers = isTeamA ? (teamAPlayers || []) : (teamBPlayers || []);
  const serverName = (typeof currentServer === 'string' ? currentServer : currentServer?.name || '').trim().toLowerCase();
  const serverWinningIdx = serverName
    ? winningPlayers.findIndex((p) => {
        const name = (typeof p === 'string' ? p : p?.name || '').trim().toLowerCase();
        return name && (name === serverName || serverName.startsWith(name) || name.startsWith(serverName));
      })
    : -1;

  const isServer1 = type === 'GAME' && serverWinningIdx === 0;
  const isServer2 = type === 'GAME' && serverWinningIdx === 1;

  // Process Rotation Mode Next Game Details
  const isMvpMode = mode === 'mvp' || !!nextGame;
  let rawTeam1 = [];
  let rawTeam2 = [];
  let nextServer = '';
  let nextSitsOut = '';

  if (nextGame) {
    rawTeam1 = (nextGame.team1 || []).map(p => typeof p === 'string' ? p : p?.name || '');
    rawTeam2 = (nextGame.team2 || []).map(p => typeof p === 'string' ? p : p?.name || '');
    nextServer = (typeof nextGame.server === 'string' ? nextGame.server : nextGame.server?.name) || rawTeam1[0] || '';
    nextSitsOut = (typeof nextGame.sitsOut === 'string' ? nextGame.sitsOut : nextGame.sitsOut?.name) || '';
  }

  const serverFirstName = nextServer ? nextServer.trim().split(' ')[0] : '';
  // Six-player games have two people sitting out ("Eli Ng & Fay Li" → "Eli & Fay")
  const sitsOutFirstName = nextSitsOut
    .split('&')
    .map((n) => n.trim().split(' ')[0])
    .filter(Boolean)
    .join(' & ');

  // Order teams so that the serving player's team is TOP ROW, and server is TOP-LEFT spot!
  let topRowTeam = [...rawTeam1];
  let bottomRowTeam = [...rawTeam2];

  const isServerInRawTeam2 = bottomRowTeam.some(p =>
    (typeof p === 'string' ? p : p?.name || '').trim().startsWith(serverFirstName)
  );

  if (isServerInRawTeam2) {
    topRowTeam = [...rawTeam2];
    bottomRowTeam = [...rawTeam1];
  }

  // Ensure server is index 0 of topRowTeam (Top-Left spot)
  const serverIdx = topRowTeam.findIndex(p =>
    (typeof p === 'string' ? p : p?.name || '').trim().startsWith(serverFirstName)
  );

  if (serverIdx > 0) {
    const serverPlayer = topRowTeam[serverIdx];
    topRowTeam.splice(serverIdx, 1);
    topRowTeam.unshift(serverPlayer);
  }

  // Scores from the winner's point of view
  const winnerFirst = (pair) => (isTeamA ? pair : [pair[1], pair[0]]);
  const gameScore = Array.isArray(games) ? winnerFirst(games) : null;
  const setList = Array.isArray(setScores) ? setScores.map((s) => winnerFirst(setScorePair(s))) : [];
  const lastSet = setList[setList.length - 1];
  const isBigMoment = !isMvpMode && (type === 'SET' || type === 'MATCH');

  const title = type === 'MATCH' ? 'Match won!' : type === 'SET' ? 'Set won!' : 'Game';
  const kicker = type === 'MATCH' ? 'Champions' : type === 'SET' ? `Set ${Math.max(setList.length, 1)}` : null;

  return (
    <div
      onClick={onDismiss}
      role="dialog"
      aria-live="assertive"
      aria-label={isMvpMode ? 'Next up' : `${title} ${toTitleCase(winnerName)}`}
      className={`celebrate ${visible ? 'is-in' : 'is-out'} ${isBigMoment ? 'is-big' : ''} ${type === 'MATCH' ? 'is-match' : ''}`}
      style={{ '--celebrate-duration': `${duration}ms` }}
    >
      <div className="celebrate__scrim" aria-hidden="true" />
      {!isMvpMode && type === 'MATCH' && <div className="celebrate__rays" aria-hidden="true" />}
      {!isMvpMode && type === 'MATCH' && (
        <div className="celebrate__confetti" aria-hidden="true">
          {confetti.map((c, i) => (
            <span
              key={i}
              style={{
                left: c.left,
                width: c.size,
                height: `calc(${c.size} * 1.6)`,
                background: c.colour,
                animationDelay: c.delay,
                animationDuration: c.duration,
                '--drift': c.drift,
                '--spin': c.spin,
              }}
            />
          ))}
        </div>
      )}

      {isMvpMode && nextGame ? (
        /* =========================================================
           MVP ROTATION MODE — NEXT UP (Figma 750:11983)
           ========================================================= */
        <div className="celebrate__content next-up">
          {winnerName && (
            <p className="next-up__kicker celebrate-rise" style={{ '--d': 0 }}>
              Game to {toTitleCase(winnerName.split('&').map((n) => n.trim().split(' ')[0]).join(' & '))}
            </p>
          )}

          <div className="next-up__row">
            <div className="next-up__text">
              <p className="next-up__label celebrate-slam">Next up</p>
              {serverFirstName && (
                <p className="next-up__serving celebrate-rise" style={{ '--d': 2 }}>
                  {toTitleCase(serverFirstName)} serving
                </p>
              )}
              {sitsOutFirstName && (
                <p className="next-up__sitting celebrate-rise" style={{ '--d': 3 }}>
                  {toTitleCase(sitsOutFirstName)} sitting out
                </p>
              )}
            </div>

            {/* 2x2 avatars: serving pair on top (server top-left, gold ring + badge), opponents below */}
            <div className="next-up__avatars">
              {[topRowTeam, bottomRowTeam].map((row, rowIdx) => (
                <div key={rowIdx} className="next-up__pair">
                  {row.slice(0, 2).map((player, idx) => {
                    const name = typeof player === 'string' ? player : player?.name || 'Player';
                    const isServer = rowIdx === 0 && idx === 0 && !!serverFirstName && name.trim().startsWith(serverFirstName);
                    return (
                      <div
                        key={idx}
                        className={`next-up__avatar celebrate-pop ${isServer ? 'is-server' : ''}`}
                        style={{ '--d': rowIdx * 2 + idx + 1 }}
                      >
                        <img src={getPlayerAvatar(allPlayers, name)} alt={name} />
                        {isServer && <img className="next-up__badge" src={serveBadge} alt="" width="24" height="24" />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* =========================================================
           CLASSIC MODE END OF GAME / SET / MATCH OVERLAY
           ========================================================= */
        <div className="celebrate__content celebrate-classic">
          {/* Winning player / team avatars */}
          <div className="celebrate-classic__avatars">
            <div
              className={`celebrate-pop celebration-classic-avatar celebrate-classic__avatar ${
                isServer1 ? 'is-lead' : (isServer2 ? '' : 'is-lead')
              }`}
              style={{ '--d': 0 }}
            >
              <img src={p1Avatar} alt={winnerName} />
              {isServer1 && (
                <div className="celebrate-pop celebrate-classic__badge" style={{ '--d': 2 }}>
                  <span className="material-symbols-outlined">padel</span>
                </div>
              )}
            </div>
            {p2Avatar && (
              <div
                className={`celebrate-pop celebration-classic-avatar celebrate-classic__avatar ${
                  isServer2 ? 'is-lead' : ''
                }`}
                style={{ '--d': 1 }}
              >
                <img src={p2Avatar} alt={winnerName} />
                {isServer2 && (
                  <div className="celebrate-pop celebrate-classic__badge" style={{ '--d': 2 }}>
                    <span className="material-symbols-outlined">padel</span>
                  </div>
                )}
              </div>
            )}
            {type !== 'GAME' && (
              <div className="celebrate-pop celebrate-classic__badge" style={{ '--d': 2 }}>
                <span className="material-symbols-outlined">trophy</span>
              </div>
            )}
          </div>

          {kicker && <p className="celebrate-kicker celebrate-rise" style={{ '--d': 1 }}>{kicker}</p>}

          {/* Bold announcement */}
          <h1 className="celebration-classic-title celebrate-title celebrate-slam">{title}</h1>

          {/* Winning player / team name */}
          {winnerName && (
            <div className="celebration-classic-winner celebrate-winner celebrate-rise" style={{ '--d': 3 }}>
              {toTitleCase(winnerName)}
            </div>
          )}

          {/* Score summary */}
          {type === 'GAME' && gameScore && (
            <div className="celebrate-scores celebrate-rise" style={{ '--d': 4 }}>
              <span className="celebrate-scores__label">Games</span>
              <span className="celebrate-chip is-win">{gameScore[0]}</span>
              <span className="celebrate-scores__dash">–</span>
              <span className="celebrate-chip">{gameScore[1]}</span>
            </div>
          )}
          {type === 'SET' && lastSet && (
            <div className="celebrate-scores celebrate-rise" style={{ '--d': 4 }}>
              <span className="celebrate-chip is-win">{lastSet[0]}</span>
              <span className="celebrate-scores__dash">–</span>
              <span className="celebrate-chip">{lastSet[1]}</span>
            </div>
          )}
          {type === 'MATCH' && setList.length > 0 && (
            <div className="celebrate-scores celebrate-rise" style={{ '--d': 4 }}>
              {setList.map(([w, l], i) => (
                <span key={i} className={`celebrate-chip celebrate-chip--set ${w > l ? 'is-win' : ''}`}>
                  {w}–{l}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Auto-dismiss countdown */}
      <div className="celebrate__footer" aria-hidden="true">
        <div className="celebrate__progress"><span /></div>
        <span className="celebrate__hint">Tap to continue</span>
      </div>
    </div>
  );
}
